/**
 * View-local URL params (tab, sort, inputs) next to the global state from
 * `useUrlState`. Written with `replace` so typing into an input does not
 * flood the history; `push` for tab changes so Back returns to the last tab.
 */
import { useCallback } from "react";
import { useSearchParams } from "react-router";

export type ParamPatch = Record<string, string | number | boolean | undefined | null>;

export function useViewParams() {
  const [params, setParams] = useSearchParams();
  const set = useCallback(
    (patch: ParamPatch, opts: { push?: boolean } = {}) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === null || v === "" || v === false) next.delete(k);
            else next.set(k, v === true ? "1" : String(v));
          }
          return next;
        },
        { replace: !opts.push },
      );
    },
    [setParams],
  );
  const str = (k: string, d = ""): string => params.get(k) ?? d;
  const num = (k: string): number | undefined => {
    const raw = params.get(k);
    if (raw === null || raw === "") return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };
  const bool = (k: string): boolean => {
    const raw = params.get(k);
    return raw === "1" || raw === "true";
  };
  return { params, set, str, num, bool };
}

/** Typed enum param with a default. */
export function pick<T extends string>(raw: string, allowed: readonly T[], d: T): T {
  return (allowed as readonly string[]).includes(raw) ? (raw as T) : d;
}
